import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Music, Play, ShoppingCart, X, Loader2, GraduationCap, Drum, Info, BookOpen, Clock, ArrowRight, Zap, Mic, Repeat, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { PageEditProvider, usePageEditMode } from '@/context/PageEditModeContext';
import { PageEditable } from '@/components/edit/PageEditable';
import { useHero } from '@/hooks/useHero';
import { PageEditToolbar } from '@/components/edit/PageEditToolbar';
import { LessonInlineField } from '@/components/edit/LessonInlineField';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SambaLessons = ({ embedded = false } = {}) => (
  <PageEditProvider slug="samba-lessons" group={embedded ? 'playAlong' : undefined}>
    <SambaLessonsInner embedded={embedded} />
    {!embedded && <PageEditToolbar />}
  </PageEditProvider>
);

const SambaLessonsInner = ({ embedded = false }) => {
  const hero = useHero('samba-lessons');
  const navigate = useNavigate();
  const { isAdminUser, enabled } = usePageEditMode();
  const [lessons, setLessons] = useState([]);
  const [lessonsLoading, setLessonsLoading] = useState(true);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [previewLesson, setPreviewLesson] = useState(null);
  const [expandedLesson, setExpandedLesson] = useState(null);
  const [editingVideoLesson, setEditingVideoLesson] = useState(null);
  const [videoUrlDraft, setVideoUrlDraft] = useState('');
  const [editingImageLesson, setEditingImageLesson] = useState(null);
  const [imageUrlDraft, setImageUrlDraft] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [creatingLesson, setCreatingLesson] = useState(false);
  const [booking, setBooking] = useState({
    customer_name: '',
    customer_email: '',
  });
  const [loading, setLoading] = useState(false);

  // Fetch lessons from database
  useEffect(() => {
    const fetchLessons = async () => {
      try {
        const response = await fetch(`${API}/instrument-lessons`);
        if (response.ok) {
          const data = await response.json();
          setLessons(data);
        } else {
          toast.error('Failed to load lessons');
        }
      } catch (err) {
        console.error('Error fetching lessons:', err);
        toast.error('Failed to load lessons');
      } finally {
        setLessonsLoading(false);
      }
    };
    fetchLessons();
  }, []);

  const handleBuyLesson = (lesson) => {
    setSelectedLesson(lesson);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!booking.customer_name || !booking.customer_email) {
      toast.error('Please fill in your name and email');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/lessons/purchase`, {
        lesson_id: selectedLesson.id,
        lesson_name: selectedLesson.name,
        price: selectedLesson.price,
        customer_name: booking.customer_name,
        customer_email: booking.customer_email,
      });

      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      console.error('Purchase error:', error);
      toast.error('Failed to process purchase. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (lessonsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
        >
          <Drum className="w-12 h-12 text-primary" />
        </motion.div>
      </div>
    );
  }

  return (
    <div className={embedded ? "px-6" : "min-h-screen py-12 px-6"}>
      <div className="max-w-6xl mx-auto">
        {/* Hero Section */}
        {!embedded && (
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative mb-12 rounded-3xl overflow-hidden"
        >
          <div className="absolute inset-0">
            <img loading="lazy" decoding="async"
              src="/api/media/casa-samba/library/images/samba-lessons/hero.jpg"
              alt="Samba Percussion"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-transparent" />
          </div>
          
          <div className="relative z-10 px-8 py-16 md:py-24">
            <div className="max-w-2xl">
              <div className="flex items-center gap-3 mb-4">
                <GraduationCap className="w-8 h-8 text-primary" />
                <PageEditable
                  path="hero.badge"
                  as="span"
                  fallback="Online Video Lessons"
                  className="text-sm font-bold text-primary uppercase tracking-wider"
                />
              </div>
              <h1 className="text-4xl md:text-6xl font-extrabold font-display text-foreground mb-4">
                <PageEditable path="hero.titleLine1" as="span" fallback={hero.title_top || "Beginner Samba"} />
                <PageEditable path="hero.titleLine2" as="span" fallback={hero.title_bottom || "Percussion"} className="block text-primary" />
              </h1>
              <PageEditable
                path="hero.subtitle"
                as="p"
                fallback="Learn from Brasilian Master Drummers"
                className="text-lg text-muted-foreground mb-4"
              />
              <p className="text-muted-foreground">
                <PageEditable path="hero.mestreName" as="strong" fallback="Mestre Jorge Alabe" className="text-foreground" />
                {' - '}
                <PageEditable path="hero.mestreQuote" as="span" fallback={'"The Leader of Brasilian Percussion in the U.S.A."'} />
              </p>
              <PageEditable
                path="hero.tagline"
                as="p"
                fallback="Pay once, practice forever!"
                className="text-sm text-muted-foreground mt-4 bg-white/10 inline-block px-4 py-2 rounded-full"
              />
            </div>
          </div>
        </motion.div>
        )}

        {/* Featured Course Banner */}
        {!embedded && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <div 
            onClick={() => navigate('/pandeiro-juggling')}
            className="bg-gradient-to-r from-purple-600/20 to-pink-600/20 border-2 border-purple-500/50 rounded-2xl p-6 cursor-pointer hover:border-purple-500 transition-colors group"
          >
            <div className="flex flex-col md:flex-row items-center gap-6">
              <div className="w-32 h-32 rounded-xl overflow-hidden flex-shrink-0">
                <img loading="lazy" decoding="async"
                  src="/api/media/casa-samba/library/images/samba-lessons/pandeiro-album.jpg"
                  alt="Pandeiro Course"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                />
              </div>
              <div className="flex-1 text-center md:text-left">
                <span className="text-purple-400 font-semibold text-sm uppercase tracking-wider">
                  Featured Course
                </span>
                <h3 className="text-2xl font-bold text-foreground mt-1 mb-2">
                  How to Juggle a Tambourine (Pandeiro)
                </h3>
                <p className="text-muted-foreground mb-3">
                  Complete 8-chapter video course on juggling, spinning, flipping, and playing the Pandeiro Brasilian style.
                </p>
                <div className="flex items-center justify-center md:justify-start gap-4 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <BookOpen className="w-4 h-4" />
                    8 Chapters
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    3+ Hours
                  </span>
                </div>
              </div>
              <div className="flex flex-col items-center gap-2">
                <p className="text-sm text-muted-foreground line-through">$299</p>
                <p className="text-3xl font-bold text-purple-400">$199</p>
                <p className="text-xs text-green-500 font-semibold">Save $100!</p>
                <button className="px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-full font-semibold flex items-center gap-2 transition-colors">
                  View Course
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </motion.div>
        )}

        {/* Play Along with the Master Section */}
        {!embedded && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <Link 
            to="/play-along"
            className="block bg-gradient-to-r from-amber-600/20 via-orange-600/20 to-red-600/20 border-2 border-amber-500/50 rounded-2xl p-8 cursor-pointer hover:border-amber-400 transition-all group relative overflow-hidden"
          >
            {/* Animated background pulse */}
            <div className="absolute inset-0 bg-gradient-to-r from-amber-500/10 to-transparent animate-pulse" />
            
            <div className="relative flex flex-col md:flex-row items-center gap-8">
              {/* Animated drum icon */}
              <div className="relative">
                <motion.div
                  animate={{ 
                    scale: [1, 1.1, 1],
                    rotate: [0, 5, -5, 0]
                  }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="w-32 h-32 rounded-full bg-gradient-to-br from-amber-500/30 to-orange-500/30 flex items-center justify-center"
                >
                  <Drum className="w-16 h-16 text-amber-400" />
                </motion.div>
                {/* Beat indicator dots */}
                <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
                  {[0, 1, 2, 3].map((i) => (
                    <motion.div
                      key={i}
                      animate={{ opacity: [0.3, 1, 0.3] }}
                      transition={{ duration: 0.5, delay: i * 0.125, repeat: Infinity }}
                      className="w-2 h-2 rounded-full bg-amber-400"
                    />
                  ))}
                </div>
              </div>
              
              <div className="flex-1 text-center md:text-left">
                <div className="flex items-center justify-center md:justify-start gap-2 mb-2">
                  <span className="px-3 py-1 bg-amber-500/20 text-amber-400 text-xs font-bold uppercase tracking-wider rounded-full">
                    Interactive Practice
                  </span>
                  <span className="px-3 py-1 bg-green-500/20 text-green-400 text-xs font-bold uppercase tracking-wider rounded-full">
                    New!
                  </span>
                </div>
                <h3 className="text-3xl md:text-4xl font-bold text-foreground mt-2 mb-3 font-display">
                  Play Along with the <span className="text-amber-400">Master</span>
                </h3>
                <p className="text-muted-foreground mb-4 max-w-xl">
                  Practice samba rhythms alongside Mestre Curtis Pierre. Control the tempo, 
                  loop difficult sections, and record yourself to track your progress.
                </p>
                <div className="flex flex-wrap items-center justify-center md:justify-start gap-4 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1.5 bg-white/5 px-3 py-1.5 rounded-full">
                    <Zap className="w-4 h-4 text-amber-400" />
                    Tempo Control
                  </span>
                  <span className="flex items-center gap-1.5 bg-white/5 px-3 py-1.5 rounded-full">
                    <Repeat className="w-4 h-4 text-amber-400" />
                    Loop Sections
                  </span>
                  <span className="flex items-center gap-1.5 bg-white/5 px-3 py-1.5 rounded-full">
                    <Mic className="w-4 h-4 text-amber-400" />
                    Record Yourself
                  </span>
                </div>
              </div>
              
              <div className="flex-shrink-0">
                <div className="px-8 py-4 bg-amber-500 hover:bg-amber-400 text-black rounded-full font-bold text-lg flex items-center gap-3 transition-colors group-hover:scale-105 transform duration-300">
                  <Play className="w-6 h-6" />
                  Start Practicing
                </div>
                <p className="text-center text-xs text-amber-400/70 mt-2">Starting with Surdo</p>
              </div>
            </div>
          </Link>
        </motion.div>
        )}

        {/* $2 Test Purchase Section */}
        {!embedded && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12 bg-gradient-to-r from-green-600/20 to-emerald-600/20 border-2 border-green-500/50 rounded-2xl p-6"
        >
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center">
                <Zap className="w-8 h-8 text-green-400" />
              </div>
              <div>
                <span className="text-green-400 font-semibold text-sm uppercase tracking-wider">
                  Test Purchase
                </span>
                <h3 className="text-xl font-bold text-foreground">
                  Try the Checkout Flow - $2.00
                </h3>
                <p className="text-muted-foreground text-sm">
                  Test the complete purchase experience with a $2 transaction
                </p>
              </div>
            </div>
            <button
              onClick={() => handleBuyLesson({ 
                id: 'test-lesson-2', 
                name: 'Test Lesson Purchase', 
                price: 2.00,
                description: 'Test purchase to verify checkout flow'
              })}
              className="px-8 py-3 bg-green-500 hover:bg-green-400 text-black rounded-full font-bold text-lg transition-all hover:scale-105"
              data-testid="test-buy-2-dollars"
            >
              Buy Test - $2.00
            </button>
          </div>
        </motion.div>
        )}

        {/* Lessons Grid */}
        <PageEditable
          path="chooseInstrumentHeading"
          as="h2"
          className="text-3xl font-bold font-display text-foreground mb-8"
          fallback="Choose Your Instrument Lesson"
        />

        <div className="grid md:grid-cols-2 gap-6 mb-12">
          {lessons.map((lesson, index) => (
            <motion.div
              key={lesson.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="bg-card border border-border/50 rounded-2xl overflow-hidden hover:border-primary/50 transition-colors"
            >
              <div className="p-6">
                <div className="flex gap-4">
                  <div className="w-20 h-20 rounded-xl overflow-hidden flex-shrink-0 bg-background relative group">
                    <img loading="lazy" decoding="async"
                      src={lesson.image}
                      alt={lesson.name}
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.src = '/api/media/casa-samba/library/images/samba-lessons/hero.jpg'; }}
                    />
                    {isAdminUser && enabled && (
                      <button
                        onClick={() => { setEditingImageLesson(lesson); setImageUrlDraft(lesson.image || ''); }}
                        className="absolute inset-0 flex items-center justify-center bg-black/70 text-yellow-300 text-[10px] font-bold opacity-0 group-hover:opacity-100 transition-opacity"
                        data-testid={`edit-image-${lesson.id}`}
                        title="Change lesson image"
                      >
                        <Pencil className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-foreground mb-1">
                      <LessonInlineField lesson={lesson} field="name" onSaved={(l) => setLessons(prev => prev.map(x => x.id === l.id ? l : x))} placeholder="Instrument name" />
                    </h3>
                    <p className="text-2xl font-bold text-primary">
                      <LessonInlineField lesson={lesson} field="price" type="number" prefix="$" onSaved={(l) => setLessons(prev => prev.map(x => x.id === l.id ? l : x))} placeholder="0.00" />
                    </p>
                  </div>
                  {isAdminUser && enabled && (
                    <button
                      onClick={async () => {
                        if (!window.confirm(`Delete "${lesson.name}"?`)) return;
                        try {
                          const token = localStorage.getItem('auth_token');
                          await axios.delete(`${API}/instrument-lessons/${lesson.id}`, { headers: { Authorization: `Bearer ${token}` } });
                          setLessons(prev => prev.filter(l => l.id !== lesson.id));
                          window.dispatchEvent(new CustomEvent('instrument-lessons-updated'));
                          toast.success('Lesson deleted');
                        } catch (e) {
                          toast.error(e?.response?.data?.detail || 'Delete failed');
                        }
                      }}
                      className="p-1.5 text-red-400 hover:text-red-300 hover:bg-red-500/20 rounded-full"
                      data-testid={`delete-lesson-${lesson.id}`}
                      title="Delete this lesson"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
                
                <div className="text-sm text-muted-foreground mt-4">
                  <LessonInlineField lesson={lesson} field="description" type="textarea" onSaved={(l) => setLessons(prev => prev.map(x => x.id === l.id ? l : x))} placeholder="Short description shown on the card" />
                </div>

                {expandedLesson === lesson.id && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="mt-3 p-3 bg-background rounded-lg border border-border/30"
                  >
                    <div className="text-sm text-muted-foreground">
                      <LessonInlineField lesson={lesson} field="details" type="textarea" onSaved={(l) => setLessons(prev => prev.map(x => x.id === l.id ? l : x))} placeholder="Longer details shown when the user clicks More info" />
                    </div>
                  </motion.div>
                )}

                <div className="flex items-center gap-3 mt-4">
                  <button
                    onClick={() => setExpandedLesson(expandedLesson === lesson.id ? null : lesson.id)}
                    className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <Info className="w-4 h-4" />
                    <PageEditable path={expandedLesson === lesson.id ? "labels.lessInfo" : "labels.moreInfo"} fallback={expandedLesson === lesson.id ? 'Less info' : 'More info'} />
                  </button>
                  {lesson.video_url && (
                    <button
                      onClick={() => setPreviewLesson(lesson)}
                      className="flex items-center gap-2 text-sm font-semibold text-primary hover:text-primary/80 transition-colors"
                      data-testid={`preview-${lesson.id}`}
                    >
                      <Play className="w-4 h-4" />
                      <PageEditable path="labels.preview" fallback="Preview" />
                    </button>
                  )}
                  {isAdminUser && enabled && (
                    <button
                      onClick={() => { setEditingVideoLesson(lesson); setVideoUrlDraft(lesson.video_url || ''); }}
                      className="flex items-center gap-1.5 text-xs font-semibold text-yellow-400 hover:text-yellow-300 transition-colors border border-yellow-400/40 rounded-full px-2 py-1"
                      data-testid={`edit-video-${lesson.id}`}
                      title="Change the preview video URL"
                    >
                      <Pencil className="w-3 h-3" />
                      {lesson.video_url ? 'Change video' : 'Add video'}
                    </button>
                  )}
                  <button
                    onClick={() => handleBuyLesson(lesson)}
                    className="ml-auto px-5 py-2.5 bg-primary hover:bg-primary/90 text-primary-foreground rounded-full font-semibold transition-all flex items-center gap-2"
                    data-testid={`buy-${lesson.id}`}
                  >
                    <ShoppingCart className="w-4 h-4" />
                    <PageEditable path="labels.buyLesson" fallback="Buy Lesson" />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Admin: Add lesson */}
        {isAdminUser && enabled && (
          <div className="mb-8 flex justify-center">
            <button
              onClick={async () => {
                const name = window.prompt('Instrument name for the new lesson?');
                if (!name) return;
                const priceRaw = window.prompt('Price (USD)?', '29.99');
                if (priceRaw == null) return;
                const price = parseFloat(priceRaw) || 0;
                setCreatingLesson(true);
                try {
                  const token = localStorage.getItem('auth_token');
                  const id = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') + '-' + Date.now().toString().slice(-4);
                  const payload = {
                    id,
                    name,
                    price,
                    description: '',
                    details: '',
                    image: '/api/media/casa-samba/library/images/samba-lessons/hero.jpg',
                    video_url: '',
                    order: lessons.length + 1,
                    duration: '',
                  };
                  await axios.post(`${API}/instrument-lessons`, payload, { headers: { Authorization: `Bearer ${token}` } });
                  toast.success('Lesson added — click Change video / image to fill it in.');
                  const res = await axios.get(`${API}/instrument-lessons`);
                  setLessons(res.data || []);
                  window.dispatchEvent(new CustomEvent('instrument-lessons-updated'));
                } catch (e) {
                  toast.error(e?.response?.data?.detail || 'Failed to add');
                } finally {
                  setCreatingLesson(false);
                }
              }}
              disabled={creatingLesson}
              className="inline-flex items-center gap-2 px-6 py-3 bg-yellow-400/20 hover:bg-yellow-400/30 border-2 border-dashed border-yellow-400/70 rounded-2xl text-yellow-100 font-semibold disabled:opacity-50"
              data-testid="add-lesson-btn"
            >
              {creatingLesson ? <Loader2 className="w-4 h-4 animate-spin" /> : <span className="text-xl leading-none">+</span>}
              Add a new lesson
            </button>
          </div>
        )}

        {/* Complete DVD Bundle */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-primary/20 to-accent/20 border-2 border-primary/50 rounded-2xl p-8 mb-8"
        >
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <PageEditable path="bundle.title" as="h3" className="text-2xl font-bold text-foreground mb-2" fallback="Complete DVD Bundle - All Lessons!" />
              <PageEditable path="bundle.description" as="p" multiline className="text-muted-foreground" fallback="Get all 10 instrument lessons at a discounted price. Master the entire samba bateria!" />
            </div>
            <div className="flex items-center gap-4">
              <div className="text-center">
                <PageEditable path="bundle.originalPrice" as="p" className="text-sm text-muted-foreground line-through" fallback="$249.90" />
                <PageEditable path="bundle.price" as="p" className="text-4xl font-bold text-primary" fallback="$149.99" />
                <PageEditable path="bundle.saveNote" as="p" className="text-sm text-green-500 font-semibold" fallback="Save $100!" />
              </div>
              <button
                onClick={() => handleBuyLesson({ id: 'complete-bundle', name: 'Complete Samba Percussion Bundle (All 10 Lessons)', price: 149.99 })}
                className="px-6 py-3 bg-primary hover:bg-primary/90 text-primary-foreground rounded-full font-bold transition-all hover:scale-105 flex items-center gap-2"
                data-testid="buy-bundle"
              >
                <ShoppingCart className="w-5 h-5" />
                <PageEditable path="labels.buyBundle" fallback="Buy Bundle" />
              </button>
            </div>
          </div>
        </motion.div>

        {/* About the Instructors */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-card border border-border/50 rounded-2xl p-8"
        >
          <PageEditable path="instructors.title" as="h2" className="text-2xl font-bold font-display text-foreground mb-4" fallback="About Your Instructors" />
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <PageEditable path="instructors.jorge.name" as="h3" className="text-lg font-bold text-primary mb-2" fallback="Mestre Jorge Alabe" />
              <PageEditable path="instructors.jorge.bio" as="p" multiline className="text-muted-foreground" fallback={"Known as \"The Leader of Brasilian Percussion in the U.S.A.\", Mestre Jorge Alabe brings decades of experience from the streets of Rio de Janeiro to your screen. His teaching style breaks down complex rhythms into easy-to-follow patterns."} />
            </div>
            <div>
              <PageEditable path="instructors.curtis.name" as="h3" className="text-lg font-bold text-primary mb-2" fallback="Mestre Curtis Pierre" />
              <PageEditable path="instructors.curtis.bio" as="p" multiline className="text-muted-foreground" fallback="Founder and Director of Escola de Samba Casa Samba with over 38 years of experience in Afro-Brasilian drumming, dance, and culture. Curtis Pierre has trained thousands of students in the authentic rhythms of Brasil." />
            </div>
          </div>
        </motion.div>
      </div>

      {/* Preview Modal */}
      <AnimatePresence>
        {previewLesson && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85 backdrop-blur-sm"
            onClick={() => setPreviewLesson(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="w-full max-w-4xl bg-black rounded-2xl overflow-hidden shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-4 py-3 bg-zinc-900 border-b border-zinc-800">
                <h3 className="text-white font-semibold truncate">
                  Preview — {previewLesson.name}
                </h3>
                <button
                  onClick={() => setPreviewLesson(null)}
                  className="p-2 hover:bg-white/10 rounded-full transition-colors"
                  aria-label="Close preview"
                >
                  <X className="w-5 h-5 text-white" />
                </button>
              </div>
              <div className="aspect-video bg-black">
                {(() => {
                  const url = previewLesson.video_url || '';
                  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
                  const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&?]+)/);
                  if (vimeoMatch) {
                    return (
                      <iframe
                        src={`https://player.vimeo.com/video/${vimeoMatch[1]}?autoplay=1`}
                        className="w-full h-full"
                        frameBorder="0"
                        allow="autoplay; fullscreen; picture-in-picture"
                        allowFullScreen
                        title={previewLesson.name}
                      />
                    );
                  }
                  if (ytMatch) {
                    return (
                      <iframe
                        src={`https://www.youtube.com/embed/${ytMatch[1]}?autoplay=1`}
                        className="w-full h-full"
                        frameBorder="0"
                        allow="autoplay; encrypted-media; picture-in-picture"
                        allowFullScreen
                        title={previewLesson.name}
                      />
                    );
                  }
                  return (
                    <video src={url} className="w-full h-full" controls autoPlay />
                  );
                })()}
              </div>
              <div className="px-4 py-3 bg-zinc-900 border-t border-zinc-800 flex items-center justify-between gap-3">
                <p className="text-sm text-zinc-400 truncate">
                  {previewLesson.description}
                </p>
                <button
                  onClick={() => {
                    const l = previewLesson;
                    setPreviewLesson(null);
                    handleBuyLesson(l);
                  }}
                  className="flex-shrink-0 px-5 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-full font-semibold flex items-center gap-2"
                >
                  <ShoppingCart className="w-4 h-4" />
                  Buy — ${previewLesson.price}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Purchase Modal */}
      <AnimatePresence>
        {showModal && selectedLesson && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-card border border-border rounded-2xl p-6 w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <PageEditable path="purchaseModal.title" as="h3" className="text-xl font-bold text-foreground" fallback="Purchase Lesson" />
                <button
                  onClick={() => setShowModal(false)}
                  className="p-2 hover:bg-white/10 rounded-full transition-colors"
                >
                  <X className="w-5 h-5 text-muted-foreground" />
                </button>
              </div>

              <div className="bg-primary/10 border border-primary/30 rounded-xl p-4 mb-6">
                <p className="text-foreground font-semibold">{selectedLesson.name}</p>
                <p className="text-2xl font-bold text-primary">${selectedLesson.price}</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    <PageEditable path="purchaseModal.nameLabel" fallback="Your Name *" />
                  </label>
                  <input
                    type="text"
                    value={booking.customer_name}
                    onChange={(e) => setBooking({ ...booking, customer_name: e.target.value })}
                    className="w-full px-4 py-3 bg-background border border-border rounded-xl text-foreground focus:outline-none focus:border-primary"
                    placeholder="Enter your name"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    <PageEditable path="purchaseModal.emailLabel" fallback="Email Address *" />
                  </label>
                  <input
                    type="email"
                    value={booking.customer_email}
                    onChange={(e) => setBooking({ ...booking, customer_email: e.target.value })}
                    className="w-full px-4 py-3 bg-background border border-border rounded-xl text-foreground focus:outline-none focus:border-primary"
                    placeholder="your@email.com"
                    required
                  />
                </div>

                <PageEditable
                  path="purchaseModal.postPurchaseNote"
                  as="p"
                  multiline
                  className="text-xs text-muted-foreground"
                  fallback="After purchase, you'll receive instant access to download the lesson video."
                />

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-4 bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl font-bold text-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <PageEditable path="purchaseModal.processing" fallback="Processing..." />
                    </>
                  ) : (
                    <>
                      <PageEditable path="purchaseModal.submitPrefix" fallback="Purchase" /> - ${selectedLesson.price}
                    </>
                  )}
                </button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Admin: change lesson video URL inline */}
      <AnimatePresence>
        {editingVideoLesson && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/80"
            onClick={() => setEditingVideoLesson(null)}
          >
            <motion.div
              initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }}
              className="w-full max-w-lg bg-stone-900 border border-yellow-400/50 rounded-2xl p-5 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-bold text-white mb-1">Change video URL</h3>
              <p className="text-xs text-muted-foreground mb-4">
                {editingVideoLesson.name} — paste any Vimeo, YouTube, or direct MP4 URL.
              </p>
              <input
                autoFocus
                value={videoUrlDraft}
                onChange={(e) => setVideoUrlDraft(e.target.value)}
                placeholder="https://vimeo.com/… or https://youtu.be/…"
                className="w-full px-3 py-2 bg-stone-800 border border-stone-700 rounded-lg text-white mb-4"
              />
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setEditingVideoLesson(null)}
                  className="px-3 py-2 text-sm text-gray-300 hover:text-white"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    try {
                      const token = localStorage.getItem('auth_token');
                      await axios.put(
                        `${API}/instrument-lessons/${editingVideoLesson.id}`,
                        { video_url: videoUrlDraft.trim() },
                        { headers: { Authorization: `Bearer ${token}` } }
                      );
                      setLessons(prev => prev.map(l => l.id === editingVideoLesson.id ? { ...l, video_url: videoUrlDraft.trim() } : l));
                      window.dispatchEvent(new CustomEvent('instrument-lessons-updated'));
                      toast.success('Video URL saved');
                      setEditingVideoLesson(null);
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || 'Save failed');
                    }
                  }}
                  className="px-4 py-2 text-sm bg-yellow-400 hover:bg-yellow-300 text-black font-bold rounded-lg"
                >
                  Save
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Admin: change lesson image inline */}
      <AnimatePresence>
        {editingImageLesson && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/80"
            onClick={() => setEditingImageLesson(null)}
          >
            <motion.div
              initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }}
              className="w-full max-w-lg bg-stone-900 border border-yellow-400/50 rounded-2xl p-5 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-bold text-white mb-1">Change lesson image</h3>
              <p className="text-xs text-muted-foreground mb-4">{editingImageLesson.name}</p>
              {imageUrlDraft && (
                <img loading="lazy" decoding="async" src={imageUrlDraft} alt="preview" className="w-full h-32 object-cover rounded-lg mb-3 bg-stone-800" />
              )}
              <div className="border border-dashed border-stone-700 rounded-lg p-3 mb-3 text-center">
                <input
                  id="lesson-image-file"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    setUploadingImage(true);
                    try {
                      const token = localStorage.getItem('auth_token');
                      const fd = new FormData();
                      fd.append('file', file);
                      const res = await axios.post(`${API}/admin/hero-slides/upload`, fd, { headers: { Authorization: `Bearer ${token}` } });
                      const url = res.data?.url;
                      if (!url) throw new Error('No URL returned');
                      setImageUrlDraft(url);
                      toast.success('Uploaded — click Save');
                    } catch (err) {
                      toast.error(err?.response?.data?.detail || 'Upload failed');
                    } finally {
                      setUploadingImage(false);
                    }
                  }}
                />
                <label
                  htmlFor="lesson-image-file"
                  className={`inline-flex items-center gap-2 px-4 py-2 bg-yellow-400 hover:bg-yellow-300 text-black font-bold rounded-lg text-sm cursor-pointer ${uploadingImage ? 'opacity-50 pointer-events-none' : ''}`}
                >
                  {uploadingImage ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  Upload from device
                </label>
              </div>
              <label className="text-xs text-muted-foreground mb-1 block">Or paste image URL</label>
              <input
                value={imageUrlDraft}
                onChange={(e) => setImageUrlDraft(e.target.value)}
                placeholder="https://…"
                className="w-full px-3 py-2 bg-stone-800 border border-stone-700 rounded-lg text-white mb-4"
              />
              <div className="flex justify-end gap-2">
                <button onClick={() => setEditingImageLesson(null)} className="px-3 py-2 text-sm text-gray-300 hover:text-white">Cancel</button>
                <button
                  onClick={async () => {
                    try {
                      const token = localStorage.getItem('auth_token');
                      await axios.put(
                        `${API}/instrument-lessons/${editingImageLesson.id}`,
                        { image: imageUrlDraft.trim() },
                        { headers: { Authorization: `Bearer ${token}` } }
                      );
                      setLessons(prev => prev.map(l => l.id === editingImageLesson.id ? { ...l, image: imageUrlDraft.trim() } : l));
                      window.dispatchEvent(new CustomEvent('instrument-lessons-updated'));
                      toast.success('Image saved');
                      setEditingImageLesson(null);
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || 'Save failed');
                    }
                  }}
                  className="px-4 py-2 text-sm bg-yellow-400 hover:bg-yellow-300 text-black font-bold rounded-lg"
                >
                  Save
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SambaLessons;
